import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { Upload, FileText, Image, Type, ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const UploadPage = () => {
  const navigate = useNavigate();
  const [uploadType, setUploadType] = useState('file');
  const [textContent, setTextContent] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [userEmail, setUserEmail] = useState('');
  const [uploading, setUploading] = useState(false);
  const [consent, setConsent] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Check for valid purchased package on page load
  React.useEffect(() => {
    const checkPackageAccess = async () => {
      // Check URL parameters for admin testing or payment completion
      const urlParams = new URLSearchParams(window.location.search);
      
      // Admin bypass for testing - check URL for admin testing mode
      if (urlParams.get('admin_testing') === 'true') {
        setSelectedPackage('501_2500'); // Large package for testing 800+ page documents
        return;
      }

      // Check for purchased package in localStorage
      const packageFromStorage = localStorage.getItem('purchased_package');
      if (packageFromStorage) {
        setSelectedPackage(packageFromStorage);
        return;
      }

      // Check if user just completed payment (URL might have success parameters)
      const sessionId = urlParams.get('session_id');
      
      if (sessionId) {
        try {
          // Try to get payment status to retrieve package_id
          const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/status/${sessionId}`, {
            headers: {
              // No auth required for status check
            }
          });
          if (response.ok) {
            const data = await response.json();
            if (data.payment_status === 'paid' && data.package_id) {
              localStorage.setItem('purchased_package', data.package_id);
              setSelectedPackage(data.package_id);
              // Clean up URL
              window.history.replaceState({}, document.title, '/upload?testing=enabled');
              return;
            }
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
        }
      }

      // No valid purchased package, redirect to pricing
      toast.error('Please purchase a package first');
      navigate('/pricing');
    };

    checkPackageAccess();
  }, [navigate]);

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!termsAccepted) {
      toast.error('Please agree to the Terms & Conditions before uploading.');
      return;
    }

    if (!privacyAccepted) {
      toast.error('Please agree to the Privacy Policy before uploading.');
      return;
    }

    if (!ageConfirmed) {
      toast.error('Please confirm that you are 18 years of age or older before uploading.');
      return;
    }

    if (!consent) {
      toast.error('Please consent to processing communications before uploading.');
      return;
    }

    if (!acceptedFiles || acceptedFiles.length === 0) return;

    // Validate file types
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    const invalidFiles = acceptedFiles.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      toast.error('Please upload only PDF or image files (JPEG, PNG).');
      return;
    }

    // Get file size limits based on package
    const purchasedPackage = localStorage.getItem('purchased_package') || '0_500';
    const packageSizeLimits = {
      '0_500': 10 * 1024 * 1024,      // 10MB for Essential
      '501_2500': 30 * 1024 * 1024,   // 30MB for Professional
      '2501_plus': 50 * 1024 * 1024   // 50MB for Enterprise
    };
    const maxSingleSize = packageSizeLimits[purchasedPackage] || 10 * 1024 * 1024;
    const maxSizeMB = Math.round(maxSingleSize / (1024 * 1024));
    const maxTotalSize = maxSingleSize; // Total limit same as single file limit
    
    const oversizedFiles = acceptedFiles.filter(file => file.size > maxSingleSize);
    if (oversizedFiles.length > 0) {
      toast.error(`Some files exceed ${maxSizeMB}MB limit for your package: ${oversizedFiles.map(f => f.name).join(', ')}`);
      return;
    }
    
    const totalSize = acceptedFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalSize > maxTotalSize) {
      toast.error(`Total file size (${Math.round(totalSize / 1024 / 1024)}MB) exceeds ${maxSizeMB}MB limit for your package.`);
      return;
    }

    // For multiple images, directly upload without preview
    if (acceptedFiles.length > 1 || (acceptedFiles.length === 1 && acceptedFiles[0].type.startsWith('image/'))) {
      setSelectedFiles(acceptedFiles);
      await handleMultipleFilesUpload(acceptedFiles);
    } else {
      // Single PDF - use original logic
      const file = acceptedFiles[0];
      await handleSingleFileUpload(file);
    }
  }, [termsAccepted, privacyAccepted, ageConfirmed, consent, userEmail, navigate]);

  const handleSingleFileUpload = async (file) => {
    setUploading(true);
    try {
      console.log(`Uploading single file: ${file.name}, Size: ${Math.round(file.size / 1024 / 1024)}MB, Type: ${file.type}`);
      
      const formData = new FormData();
      formData.append('file', file);
      formData.append('package_id', selectedPackage);
      if (userEmail && userEmail.trim()) formData.append('user_email', userEmail.trim());

      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Session expired. Please sign in again.');
        navigate('/signin');
        setUploading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log(`Upload response status: ${response.status}`);
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          toast.error('Session expired. Please sign in again.');
          navigate('/signin');
          throw new Error('Session expired');
        }

        let errorMessage = 'Upload failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        console.error(`Upload failed with status ${response.status}:`, errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Upload successful:', result);
      
      toast.success('File uploaded successfully! Processing started.');
      navigate(`/results/${result.request_id}`);
    } catch (error) {
      console.error('Upload error full object:', error);
      console.error('Error type:', typeof error);
      console.error('Error keys:', Object.keys(error));
      console.error('Error message:', error.message);
      console.error('Error detail:', error.detail);
      console.error('Error string:', String(error));
      
      let errorMessage = 'Upload failed. Please try again.';
      if (typeof error === 'string') {
        errorMessage = error;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.detail) {
        errorMessage = error.detail;
      } else if (error.toString && error.toString() !== '[object Object]') {
        errorMessage = error.toString();
      }
      
      toast.error(`Upload failed: ${errorMessage}. Please check console for details.`);
    } finally {
      setUploading(false);
    }
  };

  const handleMultipleFilesUpload = async (files) => {
    setUploading(true);
    try {
      console.log(`Uploading ${files.length} files:`, files.map(f => `${f.name} (${Math.round(f.size / 1024 / 1024)}MB)`));
      
      const formData = new FormData();
      
      // Append all files
      files.forEach((file) => {
        formData.append('files', file);
      });
      
      formData.append('package_id', selectedPackage);
      if (userEmail && userEmail.trim()) formData.append('user_email', userEmail.trim());

      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Session expired. Please sign in again.');
        navigate('/signin');
        setUploading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log(`Multiple files upload response status: ${response.status}`);
      
      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          toast.error('Session expired. Please sign in again.');
          navigate('/signin');
          throw new Error('Session expired');
        }

        let errorMessage = 'Upload failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        console.error(`Upload failed with status ${response.status}:`, errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Multiple files upload successful:', result);
      
      toast.success(`${files.length} files uploaded successfully! Processing started.`);
      navigate(`/results/${result.request_id}`);
    } catch (error) {
      console.error('Multiple files upload error:', error);
      const errorMessage = error.message || error.detail || String(error) || 'Upload failed. Please try again.';
      toast.error(`Upload failed: ${errorMessage}. Please try again or contact support.`);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png']
    },
    multiple: true,  // Allow multiple files
    disabled: uploading
  });

  const handleTextSubmit = async () => {
    if (!textContent.trim()) {
      toast.error('Please enter some text content.');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please agree to the Terms & Conditions before submitting.');
      return;
    }

    if (!privacyAccepted) {
      toast.error('Please agree to the Privacy Policy before submitting.');
      return;
    }

    if (!ageConfirmed) {
      toast.error('Please confirm that you are 18 years of age or older before submitting.');
      return;
    }

    if (!consent) {
      toast.error('Please consent to processing communications before submitting.');
      return;
    }

    setUploading(true);

    try {
      console.log(`Submitting text content: ${textContent.length} characters`);
      
      const formData = new FormData();
      formData.append('text_content', textContent);
      formData.append('package_id', selectedPackage);
      if (userEmail && userEmail.trim()) formData.append('user_email', userEmail.trim());

      const token = localStorage.getItem('access_token');
      if (!token) {
        toast.error('Session expired. Please sign in again.');
        navigate('/signin');
        setUploading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      console.log(`Text submission response status: ${response.status}`);

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('access_token');
          toast.error('Session expired. Please sign in again.');
          navigate('/signin');
          throw new Error('Session expired');
        }

        let errorMessage = 'Text submission failed. Please try again.';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          const errorText = await response.text();
          errorMessage = errorText || errorMessage;
        }
        console.error(`Text submission failed with status ${response.status}:`, errorMessage);
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log('Text submission successful:', result);
      
      toast.success('Text submitted successfully! Processing started.');
      navigate(`/results/${result.request_id}`);
    } catch (error) {
      console.error('Text submission error:', error);
      const errorMessage = error.message || error.detail || String(error) || 'Submission failed. Please try again.';
      toast.error(`Submission failed: ${errorMessage}. Please try again or contact support.`);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 page-transition">
      {/* Header */}
      <div className="bg-slate-800/50 backdrop-blur-sm border-b border-slate-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/')}
              className="flex items-center space-x-2 text-gray-300 hover:text-purple-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </button>
            <div className="h-6 w-px bg-gray-600"></div>
            <h1 className="text-2xl font-bold gradient-text serif-display">Upload for Analysis</h1>
            {selectedPackage && (
              <div className="ml-auto flex items-center space-x-2 text-sm">
                <span className="text-gray-400">Package:</span>
                <span className="text-green-400 font-semibold">
                  {selectedPackage === '0_500' ? 'Essential (0-500 pages)' :
                   selectedPackage === '501_2500' ? 'Professional (501-2500 pages)' :
                   selectedPackage === '2501_plus' ? 'Enterprise (2501-5000 pages)' : 
                   selectedPackage}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Disclosures Column (Left Side) */}
          <div className="lg:col-span-1 order-2 lg:order-1">
            <div className="space-y-4">
              <h2 className="text-xl font-semibold text-white mb-4">Required Disclosures</h2>
              
              {/* Important Disclaimer */}
              <div className="p-3 bg-amber-900/20 border border-amber-600/30 rounded-lg">
                <div className="flex items-start space-x-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-amber-300 mb-1">Important Disclaimer</h4>
                    <p className="text-xs text-amber-200 leading-snug">
                      This tool uses AI pattern detection. Reports are supportive documentation to make patterns measurable, not final proof. Share results alongside your own records.
                    </p>
                  </div>
                </div>
              </div>

              {/* Terms & Conditions */}
              <div className="p-3 bg-slate-800/50 border border-slate-600/30 rounded-lg">
                <div className="flex items-start space-x-2 mb-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-blue-300 mb-1">Terms & Conditions</h4>
                    <p className="text-xs text-blue-200 leading-snug">
                      For informational purposes only. Not legal advice or therapy. No refunds except for technical failures.
                    </p>
                  </div>
                </div>
                <label className="flex items-start space-x-2 cursor-pointer pt-2 border-t border-slate-700/50">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={uploading}
                  />
                  <span className="text-xs text-blue-200">
                    I agree to the <button onClick={() => navigate('/terms')} className="underline hover:text-blue-100">Terms & Conditions</button> <span className="text-red-400">*</span>
                  </span>
                </label>
              </div>

              {/* Privacy Policy */}
              <div className="p-3 bg-slate-800/50 border border-slate-600/30 rounded-lg">
                <div className="flex items-start space-x-2 mb-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4 text-green-400" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-green-300 mb-1">Privacy Policy</h4>
                    <p className="text-xs text-green-200 leading-snug">
                      We never sell or share data. All files are processed securely in the US and automatically deleted within 24 hours.
                    </p>
                  </div>
                </div>
                <label className="flex items-start space-x-2 cursor-pointer pt-2 border-t border-slate-700/50">
                  <input
                    type="checkbox"
                    checked={privacyAccepted}
                    onChange={(e) => setPrivacyAccepted(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    disabled={uploading}
                  />
                  <span className="text-xs text-green-200">
                    I agree to the <button onClick={() => navigate('/privacy')} className="underline hover:text-green-100">Privacy Policy</button> <span className="text-red-400">*</span>
                  </span>
                </label>
              </div>

              {/* Age Confirmation */}
              <div className="p-3 bg-slate-800/50 border border-slate-600/30 rounded-lg">
                <div className="flex items-start space-x-2 mb-2">
                  <div className="flex-shrink-0 mt-0.5">
                    <AlertCircle className="w-4 h-4 text-amber-400" />
                  </div>
                  <div>
                    <h4 className="text-xs font-semibold text-amber-300 mb-1">Age Verification</h4>
                    <p className="text-xs text-amber-200 leading-snug">
                      Service intended for adults 18+ with legal capacity to consent.
                    </p>
                  </div>
                </div>
                <label className="flex items-start space-x-2 cursor-pointer pt-2 border-t border-slate-700/50">
                  <input
                    type="checkbox"
                    checked={ageConfirmed}
                    onChange={(e) => setAgeConfirmed(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
                    disabled={uploading}
                  />
                  <span className="text-xs text-amber-200">
                    I confirm I am 18+ years old <span className="text-red-400">*</span>
                  </span>
                </label>
              </div>

              {/* Consent */}
              <div className="p-3 bg-blue-900/20 border border-blue-700/30 rounded-lg">
                <label className="flex items-start space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 text-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    disabled={uploading}
                  />
                  <span className="text-xs text-blue-200">
                    I consent to processing my communications for AI pattern analysis. <span className="text-red-400">*</span>
                  </span>
                </label>
              </div>
            </div>
          </div>

          {/* Upload Area (Right Side - Larger) */}
          <div className="lg:col-span-2 order-1 lg:order-2">
            <div className="card h-full flex flex-col">
              <h2 className="text-xl font-semibold mb-6 text-white">Upload Your Documents</h2>
              
              {/* Upload Type Selector */}
              <div className="flex space-x-4 mb-8">
                <button
                  onClick={() => setUploadType('file')}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-lg border-2 transition-colors ${
                    uploadType === 'file'
                      ? 'border-purple-400 bg-purple-900/20 text-purple-300'
                      : 'border-gray-600 text-gray-300 hover:border-gray-500'
                  }`}
                >
                  <Upload className="w-5 h-5" />
                  <span>Upload File</span>
                </button>
              </div>

              {/* File Upload */}
              {uploadType === 'file' && (
                <div
                  {...getRootProps()}
                  className={`upload-area flex-grow flex flex-col justify-center min-h-[400px] ${isDragActive ? 'active' : ''} ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <input {...getInputProps()} />
                  <div className="text-center">
                    <div className="flex justify-center space-x-4 mb-6">
                      <FileText className="w-20 h-20 text-red-500 opacity-80" />
                    </div>
                    {uploading ? (
                      <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="spinner w-10 h-10 border-4"></div>
                        <span className="text-xl text-gray-600 font-medium">Uploading & Analyzing...</span>
                        <p className="text-sm text-gray-500">Please do not close this tab</p>
                      </div>
                    ) : (
                      <>
                        <p className="text-2xl font-medium text-gray-700 mb-3">
                          {isDragActive ? 'Drop your file here' : 'Drag & drop your PDF here'}
                        </p>
                        <button className="px-6 py-2 bg-purple-600 text-white rounded-full font-medium hover:bg-purple-700 transition-colors mb-6 shadow-md hover:shadow-lg">
                          Browse Files
                        </button>
                        <div className="p-4 bg-gray-50 rounded-xl inline-block border border-gray-200">
                          <p className="text-sm text-gray-500 font-medium">
                            Supported: PDF
                          </p>
                          <p className="text-xs text-gray-400 mt-1">
                            Up to {
                              selectedPackage === '2501_plus' ? '50MB' : 
                              selectedPackage === '501_2500' ? '30MB' : '10MB'
                            }
                          </p>
                        </div>
                        
                        {selectedFiles.length > 0 && (
                          <div className="mt-6 p-4 bg-blue-50 border border-blue-100 rounded-xl mx-auto max-w-md">
                            <div className="flex items-center justify-center space-x-2">
                              <CheckCircle className="w-5 h-5 text-green-500" />
                              <p className="text-blue-700 font-medium">
                                Ready to upload: {selectedFiles.length} file(s)
                              </p>
                            </div>
                            <div className="text-sm text-blue-600 mt-2">
                              {selectedFiles.map(f => f.name).join(', ')}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UploadPage;